/**
 * queues-section — the Queues section tab bar + live counts, mirroring the
 * Events section paradigm (eventsSectionTabs / useEventSectionCounts): every
 * queues-section screen renders the SAME tabs with live counts.
 * Tabs: Queues · Display Boards · Archive. ("Queue Journeys" removed
 * 2026-07-21 — journeys are embedded per queue now; see the wizard's
 * Queue pages step.)
 */
import type { NavApi } from "../../shell/router";
import { useWfResource } from "../workforce/useWorkforce";
import { listQueues, listMessageBoards, listQueueArchive, type QueueListPage } from "./queues-api";
import { t as i18nT } from "@v0y4ge/i18n-runtime";

export function queuesSectionTabs(nav: NavApi, active: string, counts: Partial<Record<string, number>> = {}) {
  return [
    { id: "queues", label: i18nT("queues.section.tabQueues", "Queues"), count: counts.queues, onClick: () => nav.go("queues") },
    { id: "boards", label: i18nT("queues.section.tabBoards", "Display Boards"), count: counts.boards, onClick: () => nav.go("queues.boards") },
    { id: "archive", label: i18nT("queues.section.tabArchive", "Archive"), count: counts.archive, onClick: () => nav.go("queues.archive") },
  ].map((t) => ({ ...t, active: t.id === active }));
}

export interface QueueSectionCounts { queues: number; boards: number; archive: number; [key: string]: number; }

export function useQueueSectionCounts(): QueueSectionCounts {
  const qs = useWfResource<QueueListPage>("qsec|queues", () => listQueues(null));
  const bd = useWfResource<{ boards: unknown[] }>("qsec|boards", () => listMessageBoards());
  const ar = useWfResource<{ entries: unknown[] }>("qsec|archive", () => listQueueArchive());
  return {
    // Envelope is `{ data, pagination }` (contract QueueListResponse), not `{ queues }` —
    // and defensively defaulted, so a failed/malformed fetch counts as 0 rather than
    // throwing during render (open task #25 crash guard).
    queues: (qs.data?.data ?? []).length,
    boards: (bd.data?.boards ?? []).length,
    archive: (ar.data?.entries ?? []).length,
  };
}

/** Clickable sortable column header (Events-table paradigm). */
export const SORT_ARROW = (on: boolean, dir: "asc" | "desc") => (on ? (dir === "asc" ? " ↑" : " ↓") : "");
